import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import notify, { AppToastContainer } from "../../components/ACS-Dashboard/ACS-Blocks/Toast";
import "react-toastify/dist/ReactToastify.css";
import { Eye, EyeOff } from "lucide-react";
import OnboardingImg from "../../assets/ACS-Images/Onboarding_Img.jpg";
import ascendLogo from "../../assets/ACS-Images/Ascend-Logo.png";

const PasswordSetup = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchParams] = useSearchParams();
  const code = searchParams.get("code");

  // Password strength checker
  const getPasswordStrength = (pwd) => {
    let score = 0;
    if (pwd.length >= 8) score++;
    if (/[A-Z]/.test(pwd)) score++;
    if (/[0-9]/.test(pwd)) score++;
    if (/[^A-Za-z0-9]/.test(pwd)) score++;
    return score;
  };

  const strength = getPasswordStrength(password);
  const strengthLabel =
    strength <= 1
      ? "Weak password"
      : strength === 2
      ? "Fair password"
      : strength === 3
      ? "Good password"
      : "Strong password";
  const strengthColor =
    strength <= 1
      ? "text-red-500"
      : strength === 2
      ? "text-yellow-500"
      : strength === 3
      ? "text-blue-500"
      : "text-green-600";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      notify.error({ title: "Mismatch", message: "Passwords do not match." });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/onboarding/update-password`,
                null,
                {
                    params: {
                        code: code,
                        password: password,
                        confirmPassword: confirmPassword,
                    },
                    headers: {
                        "Accept": "*/*",
                        "Content-Type": "application/json",
                    },
                }
      );

      if (response.status === 200) {
        window.location.href = `/login?msg=${encodeURIComponent(
          "Password updated successfully!"
        )}&type=success`;
      }
    } catch (error) {
      window.location.href = `/login?msg=${encodeURIComponent(
        error.response?.data?.message || "Invalid code. Try again."
      )}&type=error`;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={`flex flex-col md:flex-row min-h-screen w-full bg-white transition-opacity duration-500 overflow-x-hidden`}>
      {/* Left Side */}
      <div 
        className="hidden md:block w-full md:w-1/2 h-screen bg-cover bg-center bg-no-repeat fixed"
        style={{ backgroundImage: `url(${OnboardingImg})` }}
      >
      </div>

      {/* Right Side */}
      <div className="w-full md:w-1/2 flex flex-col items-center justify-center p-4 xs:p-5 sm:p-6 ml-0 md:ml-[50%]">
        <div className="max-w-[90vw] xs:max-w-[85vw] sm:max-w-sm w-full">
          {/* Logo + Heading */}
          <div className="text-center space-y-fluid-2 mb-fluid-3">
            <img
              src={ascendLogo}
              alt="Ascend Logo"
              className="w-[clamp(100px,15vw,160px)] mx-auto mb-fluid-2 transition-transform duration-300 hover:scale-105"
            />
            <h1 className="text-fluid-xl font-bold leading-tight @md:text-fluid-2xl">
              Set Your Password
            </h1>
            <p className="text-fluid-sm text-gray-600">
              Secure your account to get started
            </p>
          </div>

          {/* Password Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password */}
            <div className="relative">
              <label className="block text-fluid-sm font-medium mb-fluid-1">
                Password
              </label>
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                className="w-full p-fluid-1 text-fluid-sm border rounded-lg pr-10"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}

              </button>
            </div>

            {/* Strength Meter */}
            {password && (
              <div className="space-y-1">
                <div className="flex gap-1">
                  {[1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={`h-1 flex-1 rounded ${
                        i <= strength
                          ? strength === 1
                            ? "bg-red-500"
                            : strength === 2
                            ? "bg-yellow-500"
                            : strength === 3
                            ? "bg-blue-500"
                            : "bg-green-600"
                          : "bg-gray-300"
                      }`}
                    ></div>
                  ))}
                </div>
                <p className={`text-xs ${strengthColor}`}>{strengthLabel}</p>
              </div>
            )}

            {/* Confirm Password */}
            <div className="relative">
              <label className="block text-fluid-sm font-medium mb-fluid-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type={showConfirm ? "text" : "password"}
                className="w-full p-fluid-1 text-fluid-sm border rounded-lg pr-10"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              >
                {showPassword ? <Eye size={18} /> : <EyeOff size={18} />}

              </button>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={password !== confirmPassword || isSubmitting}
              className={`w-full p-2.5 text-sm font-semibold rounded-lg transition-colors flex justify-center items-center gap-2 ${
                password === confirmPassword
                  ? "bg-[#223688] text-white hover:bg-[#1c2c6d]"
                  : "bg-gray-300 text-gray-500 cursor-not-allowed"
              }`}
            >
              {isSubmitting ? "Submitting..." : "Continue"}
            </button>

            {/* Login Redirect */}
            <p className="text-center text-fluid-sm text-gray-600">
              Already have an account?{" "}
              <a
                href="/login"
                className="text-blue-600 hover:text-blue-800 font-semibold transition-colors"
              >
                Login
              </a>
            </p>
          </form>
        </div>
      </div>

      <AppToastContainer />
    </div>
  );
};

export default PasswordSetup;
